import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import { useAuth } from "context/AuthContext";
import { nguoiDungApi } from "api/nguoiDung";
import { nhanVaiTro, mauVaiTro } from "lib/nhanVaiTro";
import useTour from "hooks/useTour";
import buocHoSo from "tours/hoSo";

const RONG_MAT_KHAU = { matKhauCu: "", matKhauMoi: "", xacNhanMatKhauMoi: "" };

/**
 * Nhãn của một nhóm field trong form — cùng ngôn ngữ với nhãn "eyebrow" ở đầu
 * trang và tiêu đề cột bảng, để mắt đọc form theo từng cụm chứ không theo một
 * dãy ô nhập dài không phân đoạn.
 */
function NhanNhom({ children }) {
  return (
    <MDTypography variant="overline" color="text" display="block" lineHeight={1} mb={1.5}>
      {children}
    </MDTypography>
  );
}

NhanNhom.propTypes = {
  children: PropTypes.node.isRequired,
};

function HoSo() {
  const { user, vaiTro } = useAuth();
  const [hoSo, setHoSo] = useState(null);
  const [dangTai, setDangTai] = useState(true);

  const [loiThongTin, setLoiThongTin] = useState("");
  const [thongBaoThongTin, setThongBaoThongTin] = useState("");
  const [dangLuuThongTin, setDangLuuThongTin] = useState(false);

  const [matKhau, setMatKhau] = useState({ ...RONG_MAT_KHAU });
  const [loiMatKhau, setLoiMatKhau] = useState("");
  const [thongBaoMatKhau, setThongBaoMatKhau] = useState("");
  const [dangDoiMatKhau, setDangDoiMatKhau] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    setDangTai(true);
    const huy = nguoiDungApi.langNgheChiTiet(
      user.uid,
      (hs) => {
        setHoSo(hs);
        setDangTai(false);
      },
      () => setDangTai(false)
    );
    return huy;
  }, [user]);

  async function luuThongTin(e) {
    e.preventDefault();
    setDangLuuThongTin(true);
    setLoiThongTin("");
    setThongBaoThongTin("");
    try {
      await nguoiDungApi.capNhatThongTin(user.uid, { hoTen: hoSo.hoTen, sdt: hoSo.sdt });
      setThongBaoThongTin("Đã lưu thông tin.");
    } catch (err) {
      setLoiThongTin(err.message);
    } finally {
      setDangLuuThongTin(false);
    }
  }

  async function doiMatKhau(e) {
    e.preventDefault();
    setLoiMatKhau("");
    setThongBaoMatKhau("");
    if (matKhau.matKhauMoi.length < 6) {
      setLoiMatKhau("Mật khẩu mới phải có ít nhất 6 ký tự.");
      return;
    }
    if (matKhau.matKhauMoi !== matKhau.xacNhanMatKhauMoi) {
      setLoiMatKhau("Xác nhận mật khẩu mới không khớp.");
      return;
    }
    setDangDoiMatKhau(true);
    try {
      await nguoiDungApi.doiMatKhau(matKhau.matKhauCu, matKhau.matKhauMoi);
      setMatKhau({ ...RONG_MAT_KHAU });
      setThongBaoMatKhau("Đã đổi mật khẩu.");
    } catch (err) {
      setLoiMatKhau(
        err.code === "auth/invalid-credential" || err.code === "auth/wrong-password"
          ? "Mật khẩu cũ không đúng."
          : err.message
      );
    } finally {
      setDangDoiMatKhau(false);
    }
  }

  useTour("ho-so", dangTai || !hoSo ? [] : buocHoSo());

  if (dangTai || !hoSo) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <DauTrang nhan="Tài khoản" tieuDe="Hồ sơ của tôi" />
          <MDTypography variant="button" color="text">
            Đang tải...
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Tài khoản"
          tieuDe="Hồ sơ của tôi"
          moTa="Thông tin hiển thị cho đồng nghiệp và mật khẩu đăng nhập của bạn."
        />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card id="tour-ho-so-thong-tin" sx={{ height: "100%" }}>
              <MDBox px={2.5} pt={2.5} pb={1.5}>
                <NhanDau>Thông tin</NhanDau>
                <MDTypography variant="h6" component="h2">
                  Thông tin cá nhân
                </MDTypography>
              </MDBox>
              <Divider sx={{ my: 0 }} />

              <MDBox px={2.5} py={2.5} component="form" onSubmit={luuThongTin}>
                {loiThongTin && (
                  <MDBox mb={2}>
                    <MDAlert color="error">{loiThongTin}</MDAlert>
                  </MDBox>
                )}
                {thongBaoThongTin && (
                  <MDBox mb={2}>
                    <MDAlert color="success">{thongBaoThongTin}</MDAlert>
                  </MDBox>
                )}

                <NhanNhom>Tài khoản đăng nhập</NhanNhom>
                <MDBox mb={2}>
                  <MDInput label="Email" value={hoSo.email} fullWidth disabled />
                </MDBox>
                <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                  <MDBadge
                    badgeContent={nhanVaiTro(vaiTro)}
                    color={mauVaiTro(vaiTro)}
                    variant="gradient"
                    size="sm"
                  />
                  <MDBadge
                    badgeContent={hoSo.voHieuHoa ? "Đã vô hiệu hoá" : "Hoạt động"}
                    color={hoSo.voHieuHoa ? "secondary" : "success"}
                    variant="gradient"
                    size="sm"
                  />
                  <MDTypography variant="caption" color="text">
                    Email và vai trò do quản trị viên đặt.
                  </MDTypography>
                </MDBox>

                <Divider sx={{ my: 2.5 }} />

                <NhanNhom>Liên hệ</NhanNhom>
                <MDBox mb={2}>
                  <MDInput
                    label="Họ tên"
                    value={hoSo.hoTen ?? ""}
                    onChange={(e) => setHoSo({ ...hoSo, hoTen: e.target.value })}
                    fullWidth
                    required
                  />
                </MDBox>
                <MDBox mb={2.5}>
                  <MDInput
                    label="Số điện thoại"
                    value={hoSo.sdt ?? ""}
                    onChange={(e) => setHoSo({ ...hoSo, sdt: e.target.value })}
                    fullWidth
                  />
                </MDBox>

                <MDButton type="submit" variant="gradient" color="info" disabled={dangLuuThongTin}>
                  {dangLuuThongTin ? "Đang lưu..." : "Lưu thông tin"}
                </MDButton>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card id="tour-ho-so-doi-mat-khau" sx={{ height: "100%" }}>
              <MDBox px={2.5} pt={2.5} pb={1.5}>
                <NhanDau>Bảo mật</NhanDau>
                <MDTypography variant="h6" component="h2">
                  Đổi mật khẩu
                </MDTypography>
              </MDBox>
              <Divider sx={{ my: 0 }} />

              <MDBox px={2.5} py={2.5} component="form" onSubmit={doiMatKhau}>
                {loiMatKhau && (
                  <MDBox mb={2}>
                    <MDAlert color="error">{loiMatKhau}</MDAlert>
                  </MDBox>
                )}
                {thongBaoMatKhau && (
                  <MDBox mb={2}>
                    <MDAlert color="success">{thongBaoMatKhau}</MDAlert>
                  </MDBox>
                )}

                <NhanNhom>Xác nhận là bạn</NhanNhom>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Mật khẩu hiện tại"
                    value={matKhau.matKhauCu}
                    onChange={(e) => setMatKhau({ ...matKhau, matKhauCu: e.target.value })}
                    fullWidth
                    required
                  />
                </MDBox>

                <Divider sx={{ my: 2.5 }} />

                <NhanNhom>Mật khẩu mới</NhanNhom>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Mật khẩu mới (ít nhất 6 ký tự)"
                    value={matKhau.matKhauMoi}
                    onChange={(e) => setMatKhau({ ...matKhau, matKhauMoi: e.target.value })}
                    fullWidth
                    required
                    inputProps={{ minLength: 6 }}
                  />
                </MDBox>
                <MDBox mb={2.5}>
                  <MDInput
                    type="password"
                    label="Xác nhận mật khẩu mới"
                    value={matKhau.xacNhanMatKhauMoi}
                    onChange={(e) => setMatKhau({ ...matKhau, xacNhanMatKhauMoi: e.target.value })}
                    fullWidth
                    required
                  />
                </MDBox>

                <MDButton type="submit" variant="gradient" color="info" disabled={dangDoiMatKhau}>
                  {dangDoiMatKhau ? "Đang đổi..." : "Đổi mật khẩu"}
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default HoSo;
